import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Message } from "@/types/message";

export const useMessageEditor = (messages: Message[]) => {
  const [editingMessageIds, setEditingMessageIds] = useState<Set<string>>(new Set());
  const [workInProgressIds, setWorkInProgressIds] = useState<Set<string>>(new Set());

  const handleEditMessage = useCallback((messageId: string) => {
    setEditingMessageIds((prev) => new Set(prev).add(messageId));
  }, []);

  const handleCancelEdit = useCallback((messageId: string) => {
    setEditingMessageIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(messageId);
      return newSet;
    });
  }, []);

  const handleSaveEdit = useCallback(async (messageId: string, content: string) => {
    const { error } = await supabase
      .from("messages")
      .update({ content })
      .eq("id", messageId);

    if (error) {
      toast.error("Failed to save changes");
      return;
    }

    setWorkInProgressIds((prev) => new Set(prev).add(messageId));
  }, []);

  const handleCloseWorkInProgress = useCallback((messageId: string) => {
    setWorkInProgressIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(messageId);
      return newSet;
    });
  }, []);

  const handleFocusEditor = useCallback((messageId: string) => {
    const element = document.getElementById(`message-${messageId}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  // Automatically add messages with editor_status='wip' to editing state
  useEffect(() => {
    const wipMessageIds = messages
      .filter(m => m.editor_status === 'wip')
      .map(m => m.id);
    
    setEditingMessageIds(prev => {
      const newSet = new Set(prev);
      wipMessageIds.forEach(id => newSet.add(id));
      return newSet;
    });
  }, [messages]);

  return {
    editingMessageIds,
    workInProgressIds,
    setEditingMessageIds,
    setWorkInProgressIds,
    handleEditMessage,
    handleCancelEdit,
    handleSaveEdit,
    handleCloseWorkInProgress,
    handleFocusEditor,
  };
};
